/**
 * Telemetry Analyzer Page
 * This component provides analysis tools for telemetry data from iRacing IBT files
 */

import React, { useMemo, useState, useEffect } from 'react';
import { FuelInput } from '../components/FuelInput';
import { StaticPlannerPanel } from '../components/StaticPlannerPanel';
import { TimePicker } from '../components/TimePicker';
import {
  calculateAnalyzerLapStats,
  filterAnalyzerLaps,
  parseIBTFiles,
  type SessionData,
} from '../utils/ibtParser';
import type { FuelSavingCalibration } from '../types/strategyCalibration';

type TelemetryProfile = 'fullSpeed' | 'fuelSaving';

interface TelemetryDatasetState {
  file: File | null;
  sessionData: SessionData | null;
  error: string | null;
}

interface DatasetView {
  profile: TelemetryProfile;
  label: string;
  accentClass: string;
  file: File | null;
  sessionData: SessionData | null;
  error: string | null;
  laps: ReturnType<typeof filterAnalyzerLaps>;
  stats: ReturnType<typeof calculateAnalyzerLapStats>;
  sessionInfo: string;
}

const DATASET_CONFIG: Record<TelemetryProfile, { label: string; accentClass: string; description: string }> = {
  fullSpeed: {
    label: 'Full Speed',
    accentClass: 'text-green-400',
    description: 'Upload the reference run driven at maximum pace.',
  },
  fuelSaving: {
    label: 'Fuel Saving',
    accentClass: 'text-blue-400',
    description: 'Upload the comparison run driven with fuel saving enabled.',
  },
};

const EMPTY_DATASET_STATE: TelemetryDatasetState = {
  file: null,
  sessionData: null,
  error: null,
};


function formatLapTime(seconds: number): string {
  if (seconds <= 0) {
    return '--';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.round((seconds - mins * 60 - secs) * 1000);
  return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
}

/**
 * TelemetryAnalyzerPage component for analyzing telemetry data
 * @returns JSX element containing the telemetry analyzer interface
 */
export const TelemetryAnalyzerPage: React.FC = () => {
  function getPersistedNumber(key: string, fallback: number) {
    if (typeof window === 'undefined') return fallback;
    const v = localStorage.getItem('analyzer_' + key);
    return v !== null && !isNaN(Number(v)) ? Number(v) : fallback;
  }
  function setPersistedNumber(key: string, value: number) {
    if (typeof window !== 'undefined') localStorage.setItem('analyzer_' + key, String(value));
  }

  const [datasets, setDatasets] = useState<Record<TelemetryProfile, TelemetryDatasetState>>({
    fullSpeed: { ...EMPTY_DATASET_STATE },
    fuelSaving: { ...EMPTY_DATASET_STATE },
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [plannerRaceDuration, setPlannerRaceDuration] = useState(() => getPersistedNumber('raceDuration', 360));
  const [plannerFuelCapacity, setPlannerFuelCapacity] = useState(() => getPersistedNumber('fuelCapacity', 120));
  const [plannerPitInTime, setPlannerPitInTime] = useState(() => getPersistedNumber('pitInTime', 15));
  const [plannerPitOutTime, setPlannerPitOutTime] = useState(() => getPersistedNumber('pitOutTime', 10));
  const [plannerFuelingTime, setPlannerFuelingTime] = useState(() => getPersistedNumber('fuelingTime', 50));
  // Persist planner config
  useEffect(() => { setPersistedNumber('raceDuration', plannerRaceDuration); }, [plannerRaceDuration]);
  useEffect(() => { setPersistedNumber('fuelCapacity', plannerFuelCapacity); }, [plannerFuelCapacity]);
  useEffect(() => { setPersistedNumber('pitInTime', plannerPitInTime); }, [plannerPitInTime]);
  useEffect(() => { setPersistedNumber('pitOutTime', plannerPitOutTime); }, [plannerPitOutTime]);
  useEffect(() => { setPersistedNumber('fuelingTime', plannerFuelingTime); }, [plannerFuelingTime]);

  const handleFileSelect = (profile: TelemetryProfile, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (file && !file.name.toLowerCase().endsWith('.ibt')) {
      setDatasets((current) => ({
        ...current,
        [profile]: {
          ...current[profile],
          file: null,
          sessionData: null,
          error: 'Please select a valid .ibt file.',
        },
      }));
      return;
    }

    setDatasets((current) => ({
      ...current,
      [profile]: {
        file,
        sessionData: null,
        error: null,
      },
    }));
    setGlobalError(null);
  };

  const analyzeProfile = async (profile: TelemetryProfile, file: File): Promise<TelemetryDatasetState> => {
    try {
      const sessionData = await parseIBTFiles([file], true);
      const cleanRaceLaps = filterAnalyzerLaps(sessionData.raceLaps);

      if (cleanRaceLaps.length === 0) {
        if (sessionData.totalLaps === 0) {
          return {
            file,
            sessionData: null,
            error: 'No lap data found in this file. The file may be corrupted or unsupported.',
          };
        }

        return {
          file,
          sessionData,
          error: 'No clean race laps found. The analyzer excludes warmup, in-laps, and out-laps. Enable "Show all sessions" if you need to inspect other session types.',
        };
      }

      return {
        file,
        sessionData,
        error: null,
      };
    } catch (error) {
      console.error(`IBT processing error (${profile}):`, error);
      return {
        file,
        sessionData: null,
        error: 'Failed to process this IBT file. Please ensure it is a valid iRacing telemetry export.',
      };
    }
  };

  const processIBTFiles = async () => {
    const profilesToAnalyze = (Object.keys(DATASET_CONFIG) as TelemetryProfile[])
      .filter((profile) => datasets[profile].file !== null) as TelemetryProfile[];

    if (profilesToAnalyze.length === 0) {
      setGlobalError('Select at least one .ibt file to analyze.');
      return;
    }

    setIsProcessing(true);
    setGlobalError(null);

    try {
      const analyzedProfiles = await Promise.all(
        profilesToAnalyze.map(async (profile) => [profile, await analyzeProfile(profile, datasets[profile].file as File)] as const),
      );

      setDatasets((current) => {
        const next = { ...current };

        for (const [profile, result] of analyzedProfiles) {
          next[profile] = result;
        }

        return next;
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearProfile = (profile: TelemetryProfile) => {
    setDatasets((current) => ({
      ...current,
      [profile]: { ...EMPTY_DATASET_STATE },
    }));
  };

  const handleClearAll = () => {
    setDatasets({
      fullSpeed: { ...EMPTY_DATASET_STATE },
      fuelSaving: { ...EMPTY_DATASET_STATE },
    });
    setGlobalError(null);
  };

  const datasetViews: DatasetView[] = (Object.keys(DATASET_CONFIG) as TelemetryProfile[]).map((profile) => {
    const config = DATASET_CONFIG[profile];
    const dataset = datasets[profile];
    const cleanSessions = dataset.sessionData?.sessions.map((session) => ({
      ...session,
      cleanLaps: filterAnalyzerLaps(session.laps),
    })) || [];

    const laps = showAllSessions
      ? cleanSessions.flatMap((session) => session.cleanLaps)
      : filterAnalyzerLaps(dataset.sessionData?.raceLaps || []);

    const sessionInfo = dataset.sessionData && !showAllSessions && laps.length > 0
      ? `Race (${laps.length} clean laps)`
      : dataset.sessionData && showAllSessions
      ? `All Sessions (${cleanSessions.map((session) => `${session.sessionType}: ${session.cleanLaps.length}`).join(', ')})`
      : 'No Data';

    return {
      profile,
      label: config.label,
      accentClass: config.accentClass,
      file: dataset.file,
      sessionData: dataset.sessionData,
      error: dataset.error,
      laps,
      stats: calculateAnalyzerLapStats(laps),
      sessionInfo,
    };
  });

  const fullSpeedView = datasetViews.find((view) => view.profile === 'fullSpeed');
  const fuelSavingView = datasetViews.find((view) => view.profile === 'fuelSaving');
  const comparisonReady = Boolean(
    fullSpeedView &&
    fuelSavingView &&
    fullSpeedView.laps.length > 0 &&
    fuelSavingView.laps.length > 0,
  );

  const lapTimeDelta = comparisonReady && fullSpeedView && fuelSavingView
    ? fuelSavingView.stats.avgLapTime - fullSpeedView.stats.avgLapTime
    : 0;
  const fuelDelta = comparisonReady && fullSpeedView && fuelSavingView
    ? fullSpeedView.stats.avgFuel - fuelSavingView.stats.avgFuel
    : 0;
  const fuelSavePercent = comparisonReady && fullSpeedView && fullSpeedView.stats.avgFuel > 0
    ? (fuelDelta / fullSpeedView.stats.avgFuel) * 100
    : 0;
  const timeLossPerPercent = fuelSavePercent > 0 ? lapTimeDelta / fuelSavePercent : 0;
  const analyzerCalibration: FuelSavingCalibration | undefined = useMemo(() => {
    if (!comparisonReady || !fullSpeedView || !fuelSavingView) {
      return undefined;
    }

    if (
      fullSpeedView.stats.avgLapTime <= 0 ||
      fullSpeedView.stats.avgFuel <= 0 ||
      fuelSavingView.stats.avgLapTime <= 0 ||
      fuelSavingView.stats.avgFuel <= 0
    ) {
      return undefined;
    }

    return {
      baselineLapTime: fullSpeedView.stats.avgLapTime,
      baselineFuelPerLap: fullSpeedView.stats.avgFuel,
      saveLapTime: fuelSavingView.stats.avgLapTime,
      saveFuelPerLap: fuelSavingView.stats.avgFuel,
    };
  }, [comparisonReady, fullSpeedView, fuelSavingView]);

  const hasAnySelection = datasetViews.some((view) => view.file);
  const hasAnyData = datasetViews.some((view) => view.laps.length > 0);

  const renderDatasetCard = (view: DatasetView) => {
    const config = DATASET_CONFIG[view.profile];

    return (
      <div key={view.profile} className="bg-slate-800 p-6 rounded-xl border border-slate-700">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className={`text-xl font-semibold ${view.accentClass}`}>{view.label}</h2>
            <p className="text-sm text-slate-400 mt-1">{config.description}</p>
          </div>

          {view.file && (
            <button
              onClick={() => handleClearProfile(view.profile)}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        <label className="block mb-4">
          <input
            type="file"
            accept=".ibt"
            onChange={(event) => handleFileSelect(view.profile, event)}
            className="hidden"
          />
          <div className="flex items-center justify-center px-4 py-4 bg-slate-700 hover:bg-slate-600 rounded-lg cursor-pointer transition-colors border-2 border-dashed border-slate-600 hover:border-slate-500 text-center">
            <span className="text-slate-300 break-all">
              {view.file ? view.file.name : `Choose ${view.label.toLowerCase()} .ibt file`}
            </span>
          </div>
        </label>

        {view.error && (
          <div className="mb-4 p-3 bg-red-900/20 border border-red-500 rounded-lg text-red-400 text-sm">
            {view.error}
          </div>
        )}

        {view.laps.length > 0 && view.sessionData && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{view.sessionInfo}</h3>
              <span className="text-xs uppercase tracking-wide text-slate-400">{view.file?.name}</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-sm">Clean Laps</div>
                <div className="text-2xl font-bold text-white">{view.stats.totalLaps}</div>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-sm">Avg Lap Time</div>
                <div className="text-2xl font-bold text-white">{formatLapTime(view.stats.avgLapTime)}</div>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-sm">Best Lap</div>
                <div className="text-2xl font-bold text-green-400">{formatLapTime(view.stats.bestLapTime)}</div>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-sm">Avg Fuel</div>
                <div className="text-2xl font-bold text-white">
                  {view.stats.avgFuel > 0 ? `${view.stats.avgFuel.toFixed(2)}L` : '--'}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Lap</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Lap Time</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Sectors</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Fuel Used</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Pit</th>
                  </tr>
                </thead>
                <tbody>
                  {view.laps.map((lap) => (
                    <tr
                      key={`${view.profile}-${lap.lapNumber}`}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="py-3 px-4 text-white">{lap.lapNumber}</td>
                      <td className="py-3 px-4 text-white font-mono">{lap.lapTime}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          {lap.sectors.map((sector, idx) => (
                            <span key={idx} className="text-green-400">
                              {sector}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-white">{lap.fuelUsed}</td>
                      <td className="py-3 px-4">
                        {lap.isPitInLap && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-900/30 text-orange-400 border border-orange-700">
                            PIT IN
                          </span>
                        )}
                        {lap.isPitOutLap && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-900/30 text-blue-400 border border-blue-700">
                            PIT OUT
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Telemetry Analyzer
          </h1>
          <p className="text-slate-400">
            Analyze your iRacing telemetry data from IBT files
          </p>
        </div>

        {/* File Upload Section */}
        <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mb-6">
          <h2 className="text-xl font-semibold text-white mb-4">Upload Comparison Files</h2>
          <p className="text-sm text-slate-400 mb-4">
            Upload one full-speed telemetry file and one fuel-saving telemetry file to compare them side by side.
          </p>
          <p className="text-sm text-slate-500 mb-4">
            Analyzer results exclude in-laps, out-laps, and the first two clean laps of each session as warmup laps.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {datasetViews.map((view) => renderDatasetCard(view))}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={processIBTFiles}
              disabled={isProcessing || !hasAnySelection}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white rounded-lg font-medium transition-colors"
            >
              {isProcessing ? 'Processing...' : 'Analyze Files'}
            </button>
            <button
              onClick={handleClearAll}
              disabled={!hasAnySelection && !hasAnyData}
              className="px-6 py-3 bg-slate-600 hover:bg-slate-700 disabled:bg-slate-700/60 text-white rounded-lg font-medium transition-colors"
            >
              Clear All
            </button>

            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer ml-auto">
              <input
                type="checkbox"
                checked={showAllSessions}
                onChange={(e) => setShowAllSessions(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-2 focus:ring-blue-500"
              />
              Show all sessions (Practice, Qualify, Race)
            </label>
          </div>

          {globalError && (
            <div className="mt-4 p-3 bg-red-900/20 border border-red-500 rounded-lg text-red-400">
              {globalError}
            </div>
          )}
        </div>

        {comparisonReady && fullSpeedView && fuelSavingView && (
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-white">Comparison Summary</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Use this delta to validate full-speed and fuel-saving calibration before feeding it into the planner.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-sm">Avg Time Loss</div>
                <div className="text-2xl font-bold text-white">
                  {lapTimeDelta > 0 ? `+${lapTimeDelta.toFixed(3)}s` : `${lapTimeDelta.toFixed(3)}s`}
                </div>
                <div className="text-slate-400 text-xs mt-1">
                  {fuelSavingView.label} vs {fullSpeedView.label}
                </div>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-sm">Fuel Saved / Lap</div>
                <div className="text-2xl font-bold text-white">{fuelDelta.toFixed(3)}L</div>
                <div className="text-slate-400 text-xs mt-1">
                  {fullSpeedView.stats.avgFuel.toFixed(2)}L to {fuelSavingView.stats.avgFuel.toFixed(2)}L
                </div>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-sm">Observed Save</div>
                <div className="text-2xl font-bold text-blue-400">{fuelSavePercent.toFixed(2)}%</div>
                <div className="text-slate-400 text-xs mt-1">
                  Relative to the full-speed run
                </div>
              </div>
              <div className="p-4 bg-slate-700/50 rounded-lg">
                <div className="text-slate-400 text-sm">Time Loss per 1% Save</div>
                <div className="text-2xl font-bold text-white">
                  {fuelSavePercent > 0 ? `${timeLossPerPercent.toFixed(3)}s` : '--'}
                </div>
                <div className="text-slate-400 text-xs mt-1">
                  Useful for planner calibration
                </div>
              </div>
            </div>
          </div>
        )}

        {comparisonReady && fullSpeedView && fuelSavingView && analyzerCalibration && (
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mt-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-white">Strategy Planner</h2>
              <p className="text-sm text-slate-400 mt-1">
                Uses the full-speed telemetry as the baseline and the fuel-saving telemetry as the planner calibration reference.
              </p>
            </div>

            <div className="mb-6 rounded-lg border border-slate-700 bg-slate-900/40 p-4">
              <h3 className="text-sm font-semibold text-white mb-3">Planner Inputs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Race Duration (minutes)
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={plannerRaceDuration}
                    onChange={(event) => setPlannerRaceDuration(Number(event.target.value) || 0)}
                    className="w-full px-3 py-2 bg-slate-800 rounded-xl border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  />
                </div>
                <div>
                  <FuelInput
                    label="Fuel Capacity"
                    value={plannerFuelCapacity}
                    onChange={setPlannerFuelCapacity}
                    placeholder="120.00"
                    unit="L"
                    min={1}
                    max={250}
                  />
                </div>
                <div>
                  <TimePicker
                    label="Pit In Time"
                    value={plannerPitInTime}
                    onChange={setPlannerPitInTime}
                    placeholder="00:15.000"
                    unit="sec"
                  />
                </div>
                <div>
                  <TimePicker
                    label="Pit Out Time"
                    value={plannerPitOutTime}
                    onChange={setPlannerPitOutTime}
                    placeholder="00:10.000"
                    unit="sec"
                  />
                </div>
                <div>
                  <TimePicker
                    label="Full Fueling Time"
                    value={plannerFuelingTime}
                    onChange={setPlannerFuelingTime}
                    placeholder="00:50.000"
                    unit="sec"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="rounded border border-slate-700 bg-slate-950/40 p-3">
                  <div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Baseline from Full Speed File</div>
                  <div className="text-white font-semibold">Avg Lap: {formatLapTime(fullSpeedView.stats.avgLapTime)}</div>
                  <div className="text-white font-semibold">Avg Fuel: {fullSpeedView.stats.avgFuel.toFixed(2)}L/lap</div>
                </div>
                <div className="rounded border border-slate-700 bg-slate-950/40 p-3">
                  <div className="text-slate-400 text-xs uppercase tracking-wide mb-1">Calibration from Fuel Saving File</div>
                  <div className="text-white font-semibold">Avg Lap: {formatLapTime(fuelSavingView.stats.avgLapTime)}</div>
                  <div className="text-white font-semibold">Avg Fuel: {fuelSavingView.stats.avgFuel.toFixed(2)}L/lap</div>
                </div>
              </div>
            </div>

            {plannerRaceDuration > 0 && plannerFuelCapacity > 0 && (
              <StaticPlannerPanel
                raceDurationMinutes={plannerRaceDuration}
                fuelCapacity={plannerFuelCapacity}
                avgFuelPerLap={fullSpeedView.stats.avgFuel}
                avgLapTime={fullSpeedView.stats.avgLapTime}
                pitInTime={plannerPitInTime}
                pitOutTime={plannerPitOutTime}
                fuelingTime={plannerFuelingTime}
                prefillFuelSavingCalibration={analyzerCalibration}
              />
            )}
          </div>
        )}

        {/* Empty State */}
        {!hasAnyData && !hasAnySelection && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-12">
            <div className="text-center">
              <div className="text-6xl mb-4">📊</div>
              <h2 className="text-2xl font-bold text-white mb-2">
                No Telemetry Data
              </h2>
              <p className="text-slate-400">
                Upload full-speed and fuel-saving iRacing .ibt files to compare lap times and fuel consumption
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
